import CloseSVG from '@public/icons/close-btn.svg';
import classNames from 'classnames';
import { useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import { Privilege } from '@/entities/privilege';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Link from '@/shared/components/shared/Link';
import Popup from '@/shared/components/shared/Popup';
import Typografed from '@/shared/components/shared/Typografed';
import { tp } from '@/shared/lib/typograf';
import type { TariffWithPopupItem } from '../types';

export const TARIFFS_OPTIONS_POPUP_NAME = 'tariffs-options-popup';

export type TariffsOptionsPopupProps = {
    title?: string | null;
    allTariffFeatures: Privilege[];
    tariffs: TariffWithPopupItem[];
    discounts?: { tariffId: number; price: string }[] | null;
    discountPriceLabel?: string | null;
    futurePriceLabel?: string | null;
};

function TariffsOptionsPopup({
    title,
    allTariffFeatures,
    tariffs,
    discounts,
    discountPriceLabel,
    futurePriceLabel,
}: TariffsOptionsPopupProps) {
    const { closePopup } = useSetOpenedPopupsState();
    const tableRef = useRef<HTMLDivElement>(null);
    const tableSize = useResizeObserver({ ref: tableRef, box: 'border-box' });
    const hasDiscounts = Boolean(discounts && discounts.length > 0);
    const hasFuturePrice = tariffs.some((tariff) => tariff.futurePrice);
    const hasButtons = tariffs.some((tariff) => Boolean(tariff.button?.href && tariff.button?.text));

    return (
        <Popup
            name={TARIFFS_OPTIONS_POPUP_NAME}
            overlay={true}
            data-popup-preset="default"
            className="tariffs-options-popup"
        >
            <div className="tariffs-options-popup__inner">
                <div className="tariffs-options-popup__top">
                    {title && (
                        <div className="h3 tariffs-options-popup__title">
                            <Typografed>{title}</Typografed>
                        </div>
                    )}
                    <button
                        type="button"
                        className="tariffs-options-popup__close menu-popup-close-btn"
                        aria-label="Закрыть"
                        onClick={() => closePopup(TARIFFS_OPTIONS_POPUP_NAME)}
                    >
                        <CloseSVG />
                    </button>
                </div>

                <div className="tariffs-options-popup__table-container">
                    <div
                        ref={tableRef}
                        className="tariffs-options-popup__table"
                        role="table"
                        aria-label="tariffs comparison table"
                        style={{ '--table-height': `${tableSize.height ?? 0}px` } as React.CSSProperties}
                    >
                        <div className="tariffs-options-popup__sticky-bg" aria-hidden="true" />
                        <div className="tariffs-options-popup__table-head">
                            <div className="tariffs-options-popup__table-cell tariffs-options-popup__table-cell--sticky" />
                            {tariffs.map((tariff) => (
                                <div key={tariff.id} className="tariffs-options-popup__table-cell">
                                    <div className="text-xl tariffs-options-popup__column-title">
                                        <Typografed>{tariff.title}</Typografed>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="tariffs-options-popup__table-body">
                            {allTariffFeatures.map((feature) => (
                                <div
                                    key={feature.id}
                                    className="tariffs-options-popup__table-body__row tariffs-options-popup__table-body__row--feature"
                                    role="row"
                                >
                                    <div
                                        className="text-s tariffs-options-popup__table-cell tariffs-options-popup__table-cell--sticky"
                                        role="cell"
                                    >
                                        <Typografed>{feature.name}</Typografed>
                                    </div>
                                    {tariffs.map((tariff) => {
                                        const checked = tariff.features.includes(feature.id);
                                        const ticketValue = feature.ticketValues?.[String(tariff.id)];

                                        return (
                                            <div
                                                key={tariff.id}
                                                className="text-s tariffs-options-popup__table-cell"
                                                role="cell"
                                            >
                                                {ticketValue ? (
                                                    <span className="tariffs-options-popup__table-cell__value">
                                                        {ticketValue}
                                                    </span>
                                                ) : (
                                                    <div
                                                        className={classNames(
                                                            'tariffs-options-popup__table-cell__check',
                                                            {
                                                                'is-checked': checked,
                                                            },
                                                        )}
                                                    >
                                                        <span className="visually-hidden">
                                                            {checked ? 'да' : 'нет'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}

                            {hasDiscounts && (
                                <div
                                    role="row"
                                    className="tariffs-options-popup__table-body__row tariffs-options-popup__table-body__row-bottom"
                                >
                                    <div
                                        className="text-m tariffs-options-popup__table-cell tariffs-options-popup__table-cell--sticky"
                                        role="cell"
                                    >
                                        {discountPriceLabel ? tp(discountPriceLabel) : null}
                                    </div>
                                    {tariffs.map((tariff) => {
                                        const exactDiscount = discounts?.find(
                                            (discount) => discount.tariffId === tariff.id,
                                        );

                                        return (
                                            <div
                                                key={tariff.id}
                                                className="text-xs tariffs-options-popup__table-cell"
                                                role="cell"
                                            >
                                                <div className="text-xl">
                                                    <Typografed>{exactDiscount?.price || ''}</Typografed>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {hasFuturePrice && (
                                <div
                                    className={classNames('tariffs-options-popup__table-body__row', {
                                        'tariffs-options-popup__table-cell--only-price': !hasDiscounts,
                                    })}
                                    role="row"
                                >
                                    <div
                                        className="text-m tariffs-options-popup__table-cell tariffs-options-popup__table-cell--sticky"
                                        role="cell"
                                    >
                                        {tp(futurePriceLabel || 'Окончательная стоимость')}
                                    </div>
                                    {tariffs.map((tariff) => (
                                        <div
                                            key={tariff.id}
                                            className={classNames('text-xs tariffs-options-popup__table-cell', {
                                                'tariffs-options-popup__table-cell--final-price': hasDiscounts,
                                            })}
                                            role="cell"
                                        >
                                            <div
                                                className={classNames({
                                                    'text-l text-opacity': hasDiscounts,
                                                    'text-xl': !hasDiscounts,
                                                })}
                                            >
                                                <Typografed>{tariff.futurePrice || ''}</Typografed>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {hasButtons && (
                                <div
                                    className="tariffs-options-popup__table-body__row tariffs-options-popup__table-body__row--actions"
                                    role="row"
                                >
                                    <div
                                        className="tariffs-options-popup__table-cell tariffs-options-popup__table-cell--sticky"
                                        role="cell"
                                    />
                                    {tariffs.map((tariff) => {
                                        const button = tariff.button;
                                        const hasButton = Boolean(button?.href && button?.text);

                                        return (
                                            <div
                                                key={tariff.id}
                                                className="tariffs-options-popup__table-cell"
                                                role="cell"
                                            >
                                                {hasButton && (
                                                    <Link
                                                        href={button!.href!}
                                                        target={button!.isExternal ? '_blank' : undefined}
                                                        rel={button!.isExternal ? 'noreferrer nofollow' : undefined}
                                                        className="tariffs-options-popup__btn text-xs"
                                                    >
                                                        <Typografed>{button!.text!}</Typografed>
                                                    </Link>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    );
}

export default TariffsOptionsPopup;
